/* eslint-disable react/jsx-no-bind -- don't want to write old function() syntax for event handlers */
import type { Data } from '@ori-events/basket-updated';
import * as RequestShoppingContext from '@ori-events/request-shopping-context';
import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { Fade, Modal, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { DebouncedFunc } from 'lodash';
import debounce from 'lodash.debounce';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { MiniBag, minibagWidthStyle } from './MiniBag';
import { MiniBagContextProvider } from '~/contexts/minBagContext';
import { useBasketUpdated, useBreakpointIndex, useInitialShoppingContext } from '~/hooks';
import type { MiniBagState } from '~/models/MiniBagState';
import { initialMiniBagState } from '~/models/MiniBagState';
import { basePositionStyles, log } from '~/utils';

const topNavHeightStyle = 'var(--top-nav-height, 0)';
const toolbarPositionStyle = 'var(--ecatalogue-toolbar-position, 0)';

type ToggleMenuCallbackResult = DebouncedFunc<() => void> | undefined;

const Root = styled(Modal)<{
  ownerState: { breakpointIndex: number; isECatalogue: boolean; isVisible: boolean };
}>(({ ownerState, theme }) => ({
  ...basePositionStyles,
  position: 'fixed',
  display: 'block',
  textAlign: 'left',
  zIndex: 218,
  ...(!ownerState.isVisible && {
    left: 'auto',
  }),
  ...(((ownerState.isVisible && !ownerState.isECatalogue) ||
    (ownerState.isVisible && ownerState.isECatalogue && ownerState.breakpointIndex !== 2)) && {
    left: 0,
  }),
  ...(ownerState.isVisible &&
    ownerState.isECatalogue &&
    ownerState.breakpointIndex === 2 && {
      left: `calc(100vw - ${minibagWidthStyle})`,
    }),
  ...(!ownerState.isECatalogue && {
    top: topNavHeightStyle,
  }),
  ...(ownerState.isECatalogue &&
    ownerState.breakpointIndex === 2 && {
      top: toolbarPositionStyle,
      zIndex: theme.zIndex.drawer,
    }),
  ...(ownerState.isECatalogue &&
    ownerState.breakpointIndex !== 2 && {
      top: 0,
      zIndex: 1501,
    }),
  transition: `top var(${
    ownerState.isECatalogue
      ? '--ecatalogue-toolbar-position-animation'
      : '--top-nav-height-animation'
  }, 0.3s ease-in-out)`,
}));

export const MiniBagWrapper: FC<{ isECatalogue: boolean }> = ({ isECatalogue }) => {
  const breakpointIndex = useBreakpointIndex();
  const [miniBagState, setMiniBagState] = useState<Partial<MiniBagState>>(initialMiniBagState);
  const setState = useCallback(
    (nextState: Partial<MiniBagState>) =>
      setMiniBagState((previousState) => {
        const newState = {
          ...previousState,
          ...nextState,
        };
        log('setting new state:', newState);

        return newState;
      }),
    [],
  );

  const { isVisible = false, items = [], isDataLoaded = false, isLoading = true } = miniBagState;
  const [lastCallback, setLastCallback] = useState<ToggleMenuCallbackResult>(undefined);
  const setCallback = useCallback(
    (callback: ToggleMenuCallbackResult) =>
      setLastCallback((oldCallback) => {
        if (oldCallback) {
          log('cancelling old callback ...');
          oldCallback.cancel();
        }
        log('setting up toggle menu callback:', callback);
        callback?.();

        return callback;
      }),
    [],
  );

  log(
    'Rendering ...',
    '\n\tState:',
    miniBagState,
    '\n\tBreakpoint index:',
    breakpointIndex,
    '\n\tIs eCatalogue:',
    isECatalogue,
    '\n\tLast hover debounce callback:',
    lastCallback,
  );

  const { getTestId } = useTestId();

  const updateData = (data: Data) => {
    setState({
      isLoading: false,
      isDataLoaded: true,
      items: data.shoppingBag.shoppingBagItems.filter((item) => item.itemType !== 'U'),
    });
  };

  useInitialShoppingContext(updateData);
  useBasketUpdated(updateData);

  useEffect(() => {
    log('Requesting shopping context ...');
    const event = new CustomEvent(RequestShoppingContext.Name);
    window.dispatchEvent(event);
  }, []);

  const isECatalogueDesktop = isECatalogue && breakpointIndex === 2;
  const isOtherPageSmallScreen = !isECatalogue && breakpointIndex === 0;

  const debouncedHide = useCallback(
    () =>
      debounce(() => {
        setCallback(undefined);
        setState({ isVisible: false });
      }, 500),
    [setCallback, setState],
  );

  const toggleMenuCallback = useCallback<
    (data: ToggleMenuFlyout.Event) => DebouncedFunc<() => void> | undefined
  >(
    (data: ToggleMenuFlyout.Event) => {
      log('toggleMenuCallback - event:', data);

      if (isECatalogueDesktop || isOtherPageSmallScreen) {
        log('toggleMenuCallback: ignored in this breakpoint');

        return undefined;
      }

      if (data.detail.menuType !== ToggleMenuFlyout.MenuTypes.Minibag) {
        log('toggleMenuCallback: toggle-menu-flyout event for another component, closing ...');
        setState({ isVisible: false });

        return undefined;
      }

      if (typeof data.detail.shouldOpen === 'undefined') {
        log(
          'toggleMenuCallback: toggle-menu-flyout event without shouldOpen property set,',
          'toggling visibility ...',
        );

        setMiniBagState((previousState) => ({
          ...previousState,
          isVisible: !previousState.isVisible,
        }));

        return undefined;
      }

      if (data.detail.shouldOpen) {
        log('toggleMenuCallback: toggle-menu-flyout event with shouldOpen === true, opening ...');
        setState({ isVisible: true });

        return undefined;
      }

      log(
        'toggleMenuCallback: toggle-menu-flyout event with shouldOpen === false',
        'closing scheduled after 500 ms ...',
      );

      return debouncedHide();
    },
    [debouncedHide, isECatalogueDesktop, isOtherPageSmallScreen, setState],
  );

  useEffect(() => {
    // always show in eCatalog "desktop" mode
    if (isECatalogueDesktop) {
      log('eCatalogue and desktop - always shown: opening ...');
      setState({ isVisible: true });
    }
    // always hide in other pages small screen
    if (isOtherPageSmallScreen) {
      log('other pages and small breakpoint - always hidden: closing ...');
      setState({ isVisible: false });
    }

    const toggleMenuFlyoutHandler = ((data: ToggleMenuFlyout.Event) =>
      setCallback(toggleMenuCallback(data))) as EventListener;

    window.addEventListener(ToggleMenuFlyout.Name, toggleMenuFlyoutHandler);

    return () => window.removeEventListener(ToggleMenuFlyout.Name, toggleMenuFlyoutHandler);
  }, [
    breakpointIndex,
    isECatalogue,
    isECatalogueDesktop,
    isOtherPageSmallScreen,
    lastCallback,
    setCallback,
    setState,
    toggleMenuCallback,
  ]);

  const handleOverlayClick = () => {
    if (!isECatalogueDesktop) {
      setState({ isVisible: false });
    }
  };

  const handleMouseEnter = () => {
    if (isECatalogue || isOtherPageSmallScreen) {
      log('mouseenter: ignored in this breakpoint');

      return;
    }
    log('mouseenter');
    setCallback(undefined);
  };
  const handleMouseLeave = () => {
    if (isECatalogue || isOtherPageSmallScreen) {
      log('mouseleave: ignored in this breakpoint');

      return;
    }
    log('mouseleave: hiding ...');
    setState({ isVisible: false });
  };
  const ownerState = { breakpointIndex, isECatalogue, isVisible };

  return (
    <MiniBagContextProvider value={{ setMiniBagState }}>
      <Root
        disableEnforceFocus
        ownerState={ownerState}
        open={isVisible}
        BackdropProps={{
          onClick: handleOverlayClick,
          timeout: 0,
          role: 'presentation',
          open: isVisible && !isECatalogueDesktop,
        }}
      >
        <Fade in={isVisible}>
          <div data-testid={getTestId('fading-wrapper')}>
            <MiniBag
              breakpointIndex={breakpointIndex}
              isECatalogue={isECatalogue}
              items={items}
              isDataLoaded={isDataLoaded}
              isLoading={isLoading}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
          </div>
        </Fade>
      </Root>
    </MiniBagContextProvider>
  );
};
